<template>
  <div class="funding-form-submissions">

    <SubmissionsFilters
      :filters.sync="filters"
      :per-page="$store.state.counterOfShow"
      @update:perPage="onPerPageChange"
      @search="applyFilters"
      @reset="resetFilters"
    />

    <div class="status-type-filter">
      <div class="filter-title">حالة الطلبات:</div>
      <div class="radio-group">
        <label
          v-for="option in statusOptions"
          :key="option.value"
          class="radio-label"
          :class="{ 'radio-label-active': selectedStatus === option.value }"
        >
          <input
            type="radio"
            :value="option.value"
            v-model="selectedStatus"
            @change="handleStatusChange"
            class="radio-input"
          />
          <span class="radio-text">{{ option.label }}</span>
        </label>
      </div>
    </div>

    <SubmissionsTable
      :items="items"
      :loading="loading"
      :converting="converting"
      :rejecting="rejecting"
      :status-filter="selectedStatus"
      :selected-rows.sync="selectedRows"
      :pagination="pagination"
      :page.sync="page"
      @convert-selected="openConvertConfirm(selectedIds)"
      @convert-single="openConvertConfirm([$event])"
      @reject-selected="openRejectConfirm(selectedIds)"
      @reject-single="openRejectConfirm([$event])"
      @clear-selection="clearSelection"
      @refresh="fetchList(1)"
    />

    <v-dialog v-model="confirmDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="headline">تأكيد التحويل</v-card-title>
        <v-card-text>
          هل أنت متأكد من تحويل {{ pendingConvertIds.length }} طلب/طلبات إلى عملاء وطلبات؟
          <br />
          سيتم حذف الطلبات المعالجة بنجاح من هذه القائمة.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmDialog = false">إلغاء</v-btn>
          <v-btn color="primary" :loading="converting" @click="confirmConvert">
            تحويل
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConvertResultModal
      v-model="showResultModal"
      :result="convertResult"
      @close="onResultModalClose"
    />

    <!-- ============== Reject Dialog ============== -->
    <v-dialog v-model="rejectDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="headline">تأكيد الرفض</v-card-title>
        <v-card-text>
          هل أنت متأكد من رفض {{ pendingRejectIds.length }} طلب/طلبات؟
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rejectDialog = false">إلغاء</v-btn>
          <v-btn color="error" :loading="rejecting" @click="confirmReject">
            رفض
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <RejectResultModal
      v-model="showRejectResultModal"
      :result="rejectResult"
      @close="onRejectResultModalClose"
    />
    <!-- ============== End Reject Dialog ============== -->
  </div>
</template>

<script>
import { base } from "@/mixins/base";
import fundingFormSubmissionsService from "@/services/fundingFormSubmissions";
import SubmissionsFilters from "@/components/admin/FundingFormSubmissions/SubmissionsFilters.vue";
import SubmissionsTable from "@/components/admin/FundingFormSubmissions/SubmissionsTable.vue";
import ConvertResultModal from "@/components/admin/FundingFormSubmissions/ConvertResultModal.vue";
import RejectResultModal from "@/components/admin/FundingFormSubmissions/RejectResultModal.vue";

export default {
  name: "FundingFormSubmissions",
  mixins: [base],
  components: {
    SubmissionsFilters,
    SubmissionsTable,
    ConvertResultModal,
    RejectResultModal,
  },
  data() {
    return {
      loading: false,
      converting: false,
      items: [],
      selectedRows: [],
      page: 1,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0,
      },
      filters: {
        q: "",
        mobile: "",
        date_from: "",
        date_to: "",
      },
      selectedStatus: "pending",
      statusOptions: [
        { value: "all", label: "الكل" },
        { value: "pending", label: "قيد المراجعة" },
        { value: "rejected", label: "مرفوض" },
      ],
      convertResult: {
        success: [],
        duplicate: [],
        failed: [],
      },
      showResultModal: false,
      confirmDialog: false,
      pendingConvertIds: [],
      //============== Reject section ==============
      pendingRejectIds: [],
      rejecting: false,
      rejectDialog: false,
      rejectResult: {
        success: [],
        failed: [],
      },
      showRejectResultModal: false,
      //============== End Reject section ==============
    };
  },
  computed: {
    selectedIds() {
      return this.selectedRows
        .filter((row) => row.status === "pending")
        .map((row) => row.id);
    },
  },
  watch: {
    page(val, oldVal) {
      if (val !== oldVal) {
        this.fetchList(val);
      }
    },
    "$store.state.counterOfShow"(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.page !== 1) {
          this.page = 1;
        } else {
          this.fetchList(1);
        }
      }
    },
  },
  created() {
    this.fetchList(1);
  },
  methods: {
    parseListPayload(payload, page, perPage) {
      const total = payload.count ?? 0;

      if (Array.isArray(payload.data)) {
        const lastPage = Math.max(1, Math.ceil(total / perPage) || 1);
        const from = total > 0 ? (page - 1) * perPage + 1 : 0;
        const to = Math.min(page * perPage, total);

        return {
          items: payload.data,
          pagination: {
            current_page: page,
            last_page: lastPage,
            per_page: perPage,
            total,
            from,
            to,
          },
        };
      }

      const paginated = payload.data || {};
      return {
        items: paginated.data || [],
        pagination: {
          current_page: paginated.current_page || page,
          last_page: paginated.last_page || 1,
          per_page: paginated.per_page || perPage,
          total: paginated.total ?? total,
          from: paginated.from || 0,
          to: paginated.to || 0,
        },
      };
    },
    async fetchList(page = 1) {
      this.loading = true;
      try {
        const params = {
          page,
          per_page: this.$store.state.counterOfShow,
          ...this.filters,
        };
        if (!params.date_from) delete params.date_from;
        if (!params.date_to) delete params.date_to;
        if (this.selectedStatus && this.selectedStatus !== "all") {
          params.status = this.selectedStatus;
        }

        const { data } = await fundingFormSubmissionsService.list(params);
        const payload = data.payload || {};
        const { items, pagination } = this.parseListPayload(
          payload,
          page,
          this.$store.state.counterOfShow
        );

        this.items = items;
        this.pagination = pagination;
        this.page = pagination.current_page;
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "حدث خطأ أثناء جلب البيانات");
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.fetchList(1);
      }
    },
    resetFilters() {
      this.filters = { q: "", mobile: "", date_from: "", date_to: "" };
      this.$store.state.counterOfShow = 10;
      this.applyFilters();
    },
    handleStatusChange() {
      this.selectedRows = [];
      if (this.page !== 1) {
        this.page = 1;
      } else {
        this.fetchList(1);
      }
    },
    onPerPageChange(val) {
      if (this.$store.state.counterOfShow !== val) {
        this.$store.state.counterOfShow = val;
      }
    },
    openConvertConfirm(ids) {
      if (!ids.length) return;
      this.pendingConvertIds = [...ids];
      this.confirmDialog = true;
    },
    //============== Reject Methods ==============
    openRejectConfirm(ids) {
      if (!ids.length) return;
      this.pendingRejectIds = [...ids];
      this.rejectDialog = true;
    },

    async confirmReject() {
      if (!this.pendingRejectIds.length) return;

      this.rejecting = true;
      try {
        console.log(this.pendingRejectIds);
        const { data } = await fundingFormSubmissionsService.reject(this.pendingRejectIds);
        console.log(data);
        this.rejectResult = data.payload || { success: [], failed: [] };
        this.rejectDialog = false;
        this.showRejectResultModal = true;

        const successIds = (this.rejectResult.success || []).map((r) => r.submission_id);
        this.selectedRows = this.selectedRows.filter((row) => !successIds.includes(row.id));

        await this.fetchList(this.pagination.current_page);
      } catch (e) {
        console.log(e);
        this.showPopup("error", e.response?.data?.message || "فشل الرفض");
      } finally {
        this.rejecting = false;
        this.pendingRejectIds = [];
      }
    },
    onRejectResultModalClose() {
      this.showRejectResultModal = false;
    },
    //============== End Reject Methods ==============
    async confirmConvert() {
      if (!this.pendingConvertIds.length) return;

      this.converting = true;
      try {
        const { data } = await fundingFormSubmissionsService.convert(this.pendingConvertIds);
        this.convertResult = data.payload || { success: [], duplicate: [], failed: [] };
        this.confirmDialog = false;
        this.showResultModal = true;

        const successIds = (this.convertResult.success || []).map((r) => r.submission_id);
        this.selectedRows = this.selectedRows.filter((row) => !successIds.includes(row.id));

        await this.fetchList(this.pagination.current_page);
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "فشل التحويل");
      } finally {
        this.converting = false;
        this.pendingConvertIds = [];
      }
    },
    clearSelection() {
      this.selectedRows = [];
    },
    onResultModalClose() {
      this.showResultModal = false;
    },
  },
};
</script>

<style scoped>
.page-title {
  font-size: 1.35rem;
  font-weight: 700;
  color: #3f0e40;
}
.page-subtitle {
  font-size: 0.95rem;
  margin-top: 4px;
}
.dark .status-type-filter {
  background-color: #2f2b3c;
}

.dark .filter-title {
  color: #d5d5d5;
}

.dark .radio-label {
  border: 2px solid #211d2e;
  background-color: #211d2e;
}

.dark .radio-label:hover {
  background-color: #2b072c;
  border-color: #29062a;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(63, 14, 64, .15);
}

.dark .radio-text {
  color: #e9e9e9;
}

.status-type-filter {
  background-color: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
}

.filter-title {
  font-weight: bold;
  font-size: 16px;
  color: #3f0e40;
  margin-left: 10px;
}

.radio-group {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
}

.radio-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  padding: 10px 20px;
  border: 2px solid #3f0e40;
  border-radius: 8px;
  background-color: #fff;
  transition: all 0.3s ease;
  min-width: 100px;
  text-align: center;
}

.radio-label:hover {
  background-color: #f8f5f9;
  border-color: #5a2d5b;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(63, 14, 64, 0.15);
}

.radio-input {
  position: absolute;
  opacity: 0;
  cursor: pointer;
  width: 0;
  height: 0;
}

.radio-text {
  font-size: 14px;
  color: #3f0e40;
  font-weight: 500;
  user-select: none;
  pointer-events: none;
  transition: all 0.3s ease;
}

.radio-label-active,
.radio-label:has(.radio-input:checked) {
  background-color: #3f0e40;
  border-color: #3f0e40;
  box-shadow: 0 2px 6px rgba(63, 14, 64, 0.3);
}

.radio-label-active .radio-text,
.radio-label:has(.radio-input:checked) .radio-text {
  color: #fff;
  font-weight: 600;
}

.radio-label-active:hover,
.radio-label:has(.radio-input:checked):hover {
  background-color: #2a0a2b;
  border-color: #2a0a2b;
}

.radio-label:active {
  transform: translateY(0);
  box-shadow: 0 1px 2px rgba(63, 14, 64, 0.2);
}
</style>
